import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MgmtLayout from '@/components/feature/MgmtLayout';
import ScheduleTemplateEditorModal from '@/pages/management/templates/ScheduleTemplateEditorModal';
import {
  archiveScheduleTemplate,
  canSetScheduleTemplateAsDefault,
  createScheduleTemplate,
  deleteScheduleTemplate,
  duplicateScheduleTemplate,
  exportScheduleTemplateToCsv,
  fetchAllScheduleTemplates,
  isEmptyScheduleTemplate,
  isScheduleTemplateDraft,
  setDefaultScheduleTemplate,
  SCHEDULE_TEMPLATE_PROJECT_TYPES,
  type ScheduleTemplateListRow,
} from '@/lib/scheduleTemplateManagement';
import { formatDate } from '@/lib/dateUtils';

export default function ManagementTemplatesPage() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<ScheduleTemplateListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [editorTemplateId, setEditorTemplateId] = useState<string | null>(null);
  const [actionBusyId, setActionBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createType, setCreateType] = useState<string>('Development');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchAllScheduleTemplates();
      setTemplates(Array.isArray(result.templates) ? result.templates : []);
      setError(result.error);
    } catch (err) {
      console.warn('[ManagementTemplatesPage] load failed:', err);
      setTemplates([]);
      setError(err instanceof Error ? err.message : 'Failed to load schedule templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const list = Array.isArray(templates) ? templates : [];
    if (typeFilter === 'all') return list;
    return list.filter((tpl) => tpl?.project_type === typeFilter);
  }, [templates, typeFilter]);

  const hasNoTemplates = !loading && !error && templates.length === 0;
  const hasFilterEmpty = !loading && !error && templates.length > 0 && filtered.length === 0;

  const openEditor = (id: string) => setEditorTemplateId(id);
  const closeEditor = () => {
    setEditorTemplateId(null);
    void load();
  };

  const handleEditorSaved = (result?: { isDraft: boolean; itemCount: number }) => {
    setMessage({
      type: 'success',
      text: result?.isDraft ? t('templates.savedAsDraft') : t('templates.saved'),
    });
    void load();
  };

  async function handleDuplicate(tpl: ScheduleTemplateListRow) {
    const defaultName = `${tpl.name} – Copy`;
    const newName = window.prompt(t('templates.duplicatePrompt'), defaultName);
    if (!newName?.trim()) return;
    setActionBusyId(tpl.id);
    const result = await duplicateScheduleTemplate(tpl.id, newName.trim());
    setActionBusyId(null);
    if (result.error) {
      setMessage({ type: 'error', text: result.error });
      return;
    }
    setMessage({ type: 'success', text: t('templates.duplicated') });
    await load();
    if (result.templateId) setEditorTemplateId(result.templateId);
  }

  async function handleSetDefault(tpl: ScheduleTemplateListRow) {
    setActionBusyId(tpl.id);
    const result = await setDefaultScheduleTemplate(tpl.id, tpl.project_type);
    setActionBusyId(null);
    if (result.error) {
      const text = result.error.includes('at least one task')
        ? t('templates.setDefaultNeedsTasks')
        : result.error;
      setMessage({ type: 'error', text });
      return;
    }
    setMessage({ type: 'success', text: t('templates.defaultSet') });
    await load();
  }

  async function handleArchive(tpl: ScheduleTemplateListRow) {
    if (!window.confirm(t('templates.archiveConfirm', { name: tpl.name }))) return;
    setActionBusyId(tpl.id);
    const result = await archiveScheduleTemplate(tpl.id);
    setActionBusyId(null);
    if (result.error) {
      setMessage({ type: 'error', text: result.error });
      return;
    }
    setMessage({ type: 'success', text: t('templates.archived') });
    await load();
  }

  async function handleDelete(tpl: ScheduleTemplateListRow) {
    const empty = isEmptyScheduleTemplate(tpl);
    const confirmKey = empty ? 'templates.deleteEmptyConfirm' : 'templates.deleteConfirm';
    if (!window.confirm(t(confirmKey, { name: tpl.name }))) return;
    setActionBusyId(tpl.id);
    const result = await deleteScheduleTemplate(tpl.id);
    setActionBusyId(null);
    if (result.error) {
      setMessage({ type: 'error', text: result.error });
      return;
    }
    setMessage({ type: 'success', text: t('templates.deleted') });
    await load();
  }

  async function handleExport(tpl: ScheduleTemplateListRow) {
    setActionBusyId(tpl.id);
    const result = await exportScheduleTemplateToCsv(tpl.id);
    setActionBusyId(null);
    if (result.error) setMessage({ type: 'error', text: result.error });
  }

  async function handleCreate() {
    if (!createName.trim()) return;
    setActionBusyId('create');
    const result = await createScheduleTemplate({
      name: createName.trim(),
      project_type: createType,
    });
    setActionBusyId(null);
    if (result.error) {
      setMessage({ type: 'error', text: result.error });
      return;
    }
    setShowCreate(false);
    setCreateName('');
    setMessage({ type: 'success', text: t('templates.createdAsDraft') });
    await load();
    if (result.template?.id) setEditorTemplateId(result.template.id);
  }

  return (
    <MgmtLayout title={t('nav.scheduleTemplates')}>
      <div className="px-4 lg:px-8 py-6 max-w-5xl">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-lg font-bold text-[#1A1A1A]">{t('templates.pageTitle')}</h2>
            <p className="text-sm text-[#9CA3AF] mt-1">{t('templates.pageDescMulti')}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1.5 bg-[#E8621A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            {t('templates.createTemplate')}
          </button>
        </div>

        {message && (
          <div
            className={`rounded-xl px-4 py-3 text-sm mb-4 flex items-center justify-between gap-2 ${
              message.type === 'success'
                ? 'bg-[#F0FDF4] text-[#059669] border border-[#86EFAC]/40'
                : 'bg-[#FEF2F2] text-[#EF4444] border border-[#FECACA]/40'
            }`}
          >
            <span>{message.text}</span>
            <button type="button" onClick={() => setMessage(null)} className="opacity-70 hover:opacity-100">
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => setTypeFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer ${
              typeFilter === 'all' ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'
            }`}
          >
            {t('templates.filterAll')}
          </button>
          {SCHEDULE_TEMPLATE_PROJECT_TYPES.map((pt) => (
            <button
              key={pt}
              type="button"
              onClick={() => setTypeFilter(pt)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer ${
                typeFilter === pt ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'
              }`}
            >
              {pt}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl"></i>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600 text-sm">{error}</div>
        ) : hasNoTemplates ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 px-6">
            <p className="text-[#374151] font-semibold text-sm mb-2">{t('templates.emptyList')}</p>
            <p className="text-[#9CA3AF] text-xs mb-4">{t('templates.emptyListHint')}</p>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1.5 bg-[#E8621A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer"
            >
              <i className="ri-add-line"></i>
              {t('templates.createTemplate')}
            </button>
          </div>
        ) : hasFilterEmpty ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <p className="text-[#374151] font-semibold text-sm">{t('templates.emptyFilter')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((tpl) => {
              if (!tpl?.id) return null;
              const busy = actionBusyId === tpl.id;
              const itemCount = tpl.item_count ?? 0;
              const linkCount = tpl.link_count ?? 0;
              const isDraft = isScheduleTemplateDraft(tpl);
              const isEmpty = isEmptyScheduleTemplate(tpl);
              const canSetDefault = canSetScheduleTemplateAsDefault(tpl);
              const usableDefault = tpl.is_default && !isDraft;
              const archiveBlocked = usableDefault;
              const setDefaultBlockedReason = canSetDefault
                ? null
                : t('templates.setDefaultNeedsTasks');
              const statusHelp = isDraft
                ? tpl.is_default
                  ? t('templates.archiveDisabledEmptyDefault')
                  : t('templates.draftHelp')
                : archiveBlocked
                  ? t('templates.archiveDisabledDefaultWithTasks', { projectType: tpl.project_type })
                  : null;
              const updatedLabel = tpl.updated_at
                ? formatDate(tpl.updated_at)
                : tpl.created_at
                  ? formatDate(tpl.created_at)
                  : '—';
              const showDelete = isEmpty || !tpl.is_default;
              return (
                <div
                  key={tpl.id}
                  className="bg-white rounded-2xl border border-gray-100 p-4 hover:border-[#E8621A]/20 transition-colors"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="text-sm font-bold text-[#1A1A1A] truncate">{tpl.name || t('templates.untitledTemplate')}</h3>
                        {isDraft && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280]">
                            {t('templates.draftBadge')}
                          </span>
                        )}
                        {tpl.is_default && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FEF0E7] text-[#E8621A]">
                            {t('templates.defaultBadge')}
                          </span>
                        )}
                        {!isDraft && !tpl.is_default && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F0FDF4] text-[#059669]">
                            {t('templates.activeBadge')}
                          </span>
                        )}
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#F9FAFB] text-[#6B7280]">
                          {tpl.project_type}
                        </span>
                      </div>
                      <p className="text-xs text-[#9CA3AF]">
                        {t('templates.itemCount', { count: itemCount })}
                        {' · '}
                        {t('templates.linkCount', { count: linkCount })}
                        {' · '}
                        {t('templates.updatedAt', { date: updatedLabel })}
                      </p>
                      {statusHelp && (
                        <p className="text-[10px] text-[#9CA3AF] mt-1">{statusHelp}</p>
                      )}
                      {tpl.description && (
                        <p className="text-xs text-[#6B7280] mt-1 line-clamp-2">{tpl.description}</p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 flex-shrink-0">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => openEditor(tpl.id)}
                        className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-[#FEF0E7] text-[#E8621A] cursor-pointer disabled:opacity-50"
                      >
                        {t('templates.edit')}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void handleDuplicate(tpl)}
                        className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-[#374151] cursor-pointer disabled:opacity-50"
                      >
                        {t('templates.duplicate')}
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void handleExport(tpl)}
                        className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-[#374151] cursor-pointer disabled:opacity-50"
                      >
                        {t('templates.exportCsv')}
                      </button>
                      {!tpl.is_default && (
                        <button
                          type="button"
                          disabled={busy || !canSetDefault}
                          title={setDefaultBlockedReason ?? undefined}
                          onClick={() => {
                            if (!canSetDefault) return;
                            void handleSetDefault(tpl);
                          }}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-[#F0FDF4] text-[#059669] cursor-pointer disabled:opacity-50"
                        >
                          {t('templates.setDefault')}
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={busy || archiveBlocked}
                        title={archiveBlocked ? statusHelp ?? undefined : undefined}
                        onClick={() => {
                          if (archiveBlocked) return;
                          void handleArchive(tpl);
                        }}
                        className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold cursor-pointer disabled:opacity-50 ${
                          archiveBlocked
                            ? 'text-[#9CA3AF] cursor-not-allowed'
                            : 'text-[#6B7280]'
                        }`}
                      >
                        {busy ? (
                          <i className="ri-loader-4-line animate-spin"></i>
                        ) : (
                          t('templates.archive')
                        )}
                      </button>
                      {showDelete && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void handleDelete(tpl)}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-red-600 cursor-pointer disabled:opacity-50"
                        >
                          {t('templates.delete')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 flex items-center gap-2 text-xs text-[#9CA3AF]">
          <button
            type="button"
            disabled
            className="px-3 py-2 rounded-lg border border-gray-200 opacity-50 cursor-not-allowed"
            title={t('templates.importComingSoon')}
          >
            <i className="ri-upload-2-line mr-1"></i>
            {t('templates.importCsv')}
          </button>
          <span>{t('templates.importComingSoon')}</span>
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreate(false)}></div>
          <div className="relative bg-white rounded-2xl p-5 w-full max-w-md shadow-xl">
            <h3 className="font-bold text-[#1A1A1A] mb-4">{t('templates.createTemplate')}</h3>
            <label className="block text-xs font-semibold text-[#9CA3AF] uppercase mb-1.5">
              {t('templates.templateName')}
            </label>
            <input
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm mb-3"
              placeholder={t('templates.newTemplateNamePlaceholder')}
            />
            <label className="block text-xs font-semibold text-[#9CA3AF] uppercase mb-1.5">
              {t('templates.templateType')}
            </label>
            <select
              value={createType}
              onChange={(e) => setCreateType(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm mb-4 bg-white"
            >
              {SCHEDULE_TEMPLATE_PROJECT_TYPES.map((pt) => (
                <option key={pt} value={pt}>
                  {pt}
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 rounded-xl bg-gray-100 text-sm font-semibold cursor-pointer"
              >
                {t('btn.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void handleCreate()}
                disabled={actionBusyId === 'create'}
                className="px-4 py-2 rounded-xl bg-[#E8621A] text-white text-sm font-semibold cursor-pointer disabled:opacity-50"
              >
                {t('templates.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ScheduleTemplateEditorModal
        open={!!editorTemplateId}
        templateId={editorTemplateId}
        onClose={closeEditor}
        onSaved={handleEditorSaved}
      />
    </MgmtLayout>
  );
}
